import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { startOfDay } from 'date-fns';

import { ErrorMessage } from '@hookform/error-message';
import { Button, CircularProgress, Grid, Paper } from '@mui/material';

import TraceHistoryFilter from 'features/traceHistory/TraceHistoryFilter';
import MarkerInputs from './MarkerInputs';
import MarkerHistoryChart from './MarkerHistoryChart';

import { getMarkerHistory } from 'api/markerApi';
import { MarkerHistoryForm, MarkerHistoryResults } from './types';
import { useForm } from 'react-hook-form';
import { useAPICall } from 'hooks/useAPICall';
import {
  getFrequencyAndValueFromSnapshotMarker,
  getMarkersToQuery,
} from './utils/markerHistory';

import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { zonedTimeToUtc } from 'date-fns-tz';

const initialFormValues: MarkerHistoryForm = {
  fromDate: startOfDay(new Date()),
  toDate: new Date(),
  analyzerId: -1,
  snapshotId: -1,
  hideMarker1: false,
  hideMarker2: false,
  hideMarker3: false,
  hideMarker4: false,
  hideM2M1: false,
  hideM3M1: false,
  hideM4M1: false,
  marker1: { frequency: '', value: '' },
  marker2: { frequency: '', value: '' },
  marker3: { frequency: '', value: '' },
  marker4: { frequency: '', value: '' },
};

const initialResultValues: MarkerHistoryResults = {
  marker1History: [],
  marker2History: [],
  marker3History: [],
  marker4History: [],
  m2M1Delta: [],
  m3M1Delta: [],
  m4M1Delta: [],
  timeStamps: [],
  noHistoryFound: false,
};

const MarkerHistoryPage = () => {
  const { timezone, getCurrentDateTime } = useUserDateTime();
  const [searchParams] = useSearchParams();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<MarkerHistoryForm>({
    defaultValues: { ...initialFormValues, toDate: getCurrentDateTime() },
    reValidateMode: 'onSubmit',
  });

  const { data: snapshot } = useSnapshot({ snapshotId: watch('snapshotId') });
  const { markers: snapshotMarkers } = snapshot || {};

  const { safeCall: safeGetMarkerHistory, isLoading } =
    useAPICall(getMarkerHistory);

  useEffect(() => {
    if (!snapshot || !snapshotMarkers) return;
    for (let i = 0; i < snapshotMarkers.length; i++) {
      const { index, value, frequency } =
        getFrequencyAndValueFromSnapshotMarker(snapshotMarkers[i], snapshot);
      setValue(
        ('marker' + (index + 1)) as
          | 'marker1'
          | 'marker2'
          | 'marker3'
          | 'marker4',
        {
          value,
          frequency,
        }
      );
    }
  }, [
    snapshotMarkers,
    setValue,
    snapshot?.center,
    snapshot?.span,
    snapshot?.lo,
    snapshot?.lo_mode,
    snapshot,
  ]);

  const [markerHistoryResults, setMarkerHistoryResults] =
    useState<MarkerHistoryResults>(initialResultValues);

  const onSubmit = useCallback(async () => {
    const formState = getValues();

    setMarkerHistoryResults(initialResultValues);
    if (!snapshot) return;

    const markersToQuery = getMarkersToQuery(formState, snapshot);

    const history: MarkerHistoryResults | undefined =
      await safeGetMarkerHistory(
        markersToQuery,
        snapshot.id.toString(),
        zonedTimeToUtc(formState.fromDate, timezone).toISOString(),
        zonedTimeToUtc(formState.toDate, timezone).toISOString()
      );

    if (
      (history?.marker1History.length === 0 &&
        history?.marker2History.length === 0 &&
        history?.marker3History.length === 0 &&
        history?.marker4History.length === 0) ||
      !history
    ) {
      setMarkerHistoryResults({ ...initialResultValues, noHistoryFound: true });
      return;
    }

    setMarkerHistoryResults(history);
  }, [getValues, safeGetMarkerHistory, snapshot, timezone]);

  useEffect(() => {
    const analyzerId = searchParams.get('analyzerId');
    const snapshotId = searchParams.get('snapshotId');

    if (!analyzerId || !snapshotId) return;

    setValue('analyzerId', Number(analyzerId));
    setValue('snapshotId', Number(snapshotId));

    onSubmit();
  }, [searchParams, setValue, onSubmit]);

  return (
    <Grid container spacing={1}>
      <Grid container item xs={9} spacing={2}>
        <TraceHistoryFilter control={control} watch={watch} />
      </Grid>
      <Grid item xs={3}>
        <Button
          onClick={() => {
            handleSubmit(onSubmit)();
          }}
          variant='contained'
          color='primary'
          fullWidth
        >
          Get History
        </Button>
      </Grid>

      <Grid item xs={12} />
      <Grid container item>
        <Grid container item component={Paper} style={{ minHeight: '50vh' }}>
          <Grid
            item
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            style={{ height: '100%' }}
            xs
          >
            {Object.keys(errors).map((name) => (
              <ErrorMessage
                key={name}
                errors={errors}
                name={name}
                render={({ message }) => (
                  <h2 style={{ color: 'red', padding: 0, margin: 0 }}>
                    {message}
                  </h2>
                )}
              />
            ))}
            <MarkerHistoryChart
              markerData={markerHistoryResults}
              watch={watch}
            />
            {isLoading && <CircularProgress />}
            {!isLoading && markerHistoryResults.noHistoryFound && (
              <h2 style={{ color: 'red', padding: 0, margin: 0 }}>
                No marker history found.
              </h2>
            )}
          </Grid>
          <Grid item xs={3}>
            <MarkerInputs watch={watch} control={control} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MarkerHistoryPage;
